import React from 'react';

import styled from 'styled-components';

import BuilderCategoryNavBreadcrumbs from '~/grid/components/categoryNav/BuilderCategoryNavBreadcrumbs';
import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import useGridInputs from '../hooks/useGridInputs';
import useUnifiedProductGridData from '../hooks/useUnifiedProductGridData';

import DesktopGridSortDropdown from './DesktopGridSortDropdown';

const HeaderWrapper = styled.div`
  position: relative;
  display: flex;
  padding: 20px 32px 0 32px;
  margin-top: 32px;
  ${(props) => props.$productsGridV2gridV1HeaderSpacingOverride || ''}
  margin-bottom: 24px;
  justify-content: space-between;
  align-items: center;
`;

// This wrapper is used to align the breadcrumbs and the grid title.
// While the breadcrumbs are wrapped within, this wrapper should still
// render if the breadcrumbs are not rendered in support of grid title positioning.
const DesktopSideNavWrapper = styled.div`
  margin-right: 2rem;
  flex: 0 0 268px;
`;

const HeaderLabelsWrapper = styled.div`
  ${({ theme }) => theme.gridHeaderStyle};
`;

const HeaderTitle = styled.h1`
  font-weight: bold;
  font-size: 1.5rem;
  width: 100%;
`;

const SubheaderTitle = styled.h2`
  font-weight: 400;
  font-size: 1rem;
  padding-top: 8px;
`;

const DesktopGridHeader = () => {
  // search grid, ie from ConstructorSearchV1_1, do not set gridHeader/subheader,
  // and we do not want to add setting them there in case coupling useCioSearchAndGqlProducts
  // there leads to circular dependencies later down the line (at time of writing it does not)
  //
  // so instead, we set search headers here, if there are search results we use them instead of
  // the builderGridHeader in useGridInputs (ie for categories/collections from Builder pages)
  const {
    gridHeader: builderGridHeader,
    hideSort,
    productsGridV2gridV1HeaderSpacingOverride,
  } = useGridInputs();
  const { searchQuery, totalNumResults: count } = useUnifiedProductGridData();

  const searchGridHeader = (
    <FormattedMessage
      id="search_results.results_heading_search"
      defaultMessage='Results for "{searchQuery}"'
      values={{ searchQuery }}
    />
  );
  const searchGridSubheader = (
    <FormattedMessage
      id="search_results.results_heading_total_count"
      defaultMessage="{count} results"
      values={{ count }}
    />
  );

  const gridHeader = searchQuery ? searchGridHeader : builderGridHeader;
  const gridSubheader = count ? searchGridSubheader : null;

  const { enabled: showBuilderSideNav } = useHasFeature(
    featureNames.BUILDER_CATEGORY_SIDE_NAV
  );

  return (
    <HeaderWrapper
      $productsGridV2gridV1HeaderSpacingOverride={
        productsGridV2gridV1HeaderSpacingOverride
      }
    >
      <DesktopSideNavWrapper>
        {showBuilderSideNav && <BuilderCategoryNavBreadcrumbs />}
      </DesktopSideNavWrapper>
      <HeaderLabelsWrapper>
        <HeaderTitle>{gridHeader}</HeaderTitle>
        {gridSubheader && <SubheaderTitle>{gridSubheader}</SubheaderTitle>}
      </HeaderLabelsWrapper>
      {!hideSort && <DesktopGridSortDropdown />}
    </HeaderWrapper>
  );
};

export default DesktopGridHeader;
